'use client';

import { c as _c } from "react/compiler-runtime";
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';
import { cn } from '../../../shared/cn';
import { sliderStyles } from './sliderStyles';
type SliderProps = ({
  variant?: 'single' | 'double';
  color?: never;
} | {
  variant: 'none';
  color?: keyof typeof sliderStyles;
}) & {
  className?: string;
  thumbLabels?: readonly [string, string?];
} & React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>;
const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(function Slider(t0, ref) {
  const $ = _c(40);
  let ariaLabel;
  let ariaLabelledBy;
  let className;
  let color;
  let props;
  let t1;
  let thumbLabels;
  if ($[0] !== t0) {
    ({
      variant: t1,
      color,
      className,
      thumbLabels,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = ariaLabel;
    $[2] = ariaLabelledBy;
    $[3] = className;
    $[4] = color;
    $[5] = props;
    $[6] = t1;
    $[7] = thumbLabels;
  } else {
    ariaLabel = $[1];
    ariaLabelledBy = $[2];
    className = $[3];
    color = $[4];
    props = $[5];
    t1 = $[6];
    thumbLabels = $[7];
  }
  const variant = t1 === undefined ? "single" : t1;
  const effectiveColor = variant === "single" || variant === "double" ? "algoprimary" : color ?? "algoprimary";
  let t2;
  if ($[8] !== className) {
    t2 = cn("relative flex w-full touch-none select-none items-center", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = variant === "none" ? ariaLabel : undefined;
  const t4 = variant === "none" ? ariaLabelledBy : undefined;
  const t5 = sliderStyles[effectiveColor];
  let t6;
  if ($[10] !== t5.track) {
    t6 = cn("relative h-2 w-full grow overflow-hidden rounded-full", t5.track);
    $[10] = t5.track;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = sliderStyles[effectiveColor];
  let t8;
  if ($[12] !== t7.range) {
    t8 = cn("absolute h-full rounded-full", t7.range);
    $[12] = t7.range;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <SliderPrimitive.Range className={t8} />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t9) {
    t10 = <SliderPrimitive.Track className={t6}>{t9}</SliderPrimitive.Track>;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== ariaLabel || $[20] !== ariaLabelledBy || $[21] !== effectiveColor || $[22] !== thumbLabels?.[0] || $[23] !== variant) {
    t11 = variant !== "none" && <SliderPrimitive.Thumb aria-label={thumbLabels?.[0] ?? ariaLabel} aria-labelledby={ariaLabelledBy} className={cn("block h-6 w-6 rounded-full border-2 shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-backgroundInverseTertiary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:h-4 sm:w-4", sliderStyles[effectiveColor].thumb)} />;
    $[19] = ariaLabel;
    $[20] = ariaLabelledBy;
    $[21] = effectiveColor;
    $[22] = thumbLabels?.[0];
    $[23] = variant;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== ariaLabel || $[26] !== ariaLabelledBy || $[27] !== effectiveColor || $[28] !== thumbLabels?.[1] || $[29] !== variant) {
    t12 = variant === "double" && <SliderPrimitive.Thumb aria-label={thumbLabels?.[1] ?? ariaLabel} aria-labelledby={ariaLabelledBy} className={cn("block h-6 w-6 rounded-full border-2 shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-backgroundInverseTertiary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 sm:h-4 sm:w-4", sliderStyles[effectiveColor].thumb)} />;
    $[25] = ariaLabel;
    $[26] = ariaLabelledBy;
    $[27] = effectiveColor;
    $[28] = thumbLabels?.[1];
    $[29] = variant;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== props || $[32] !== ref || $[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t2 || $[37] !== t3 || $[38] !== t4) {
    t13 = <SliderPrimitive.Root data-slot="control" ref={ref} className={t2} aria-label={t3} aria-labelledby={t4} {...props}>{t10}{t11}{t12}</SliderPrimitive.Root>;
    $[31] = props;
    $[32] = ref;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
});
Slider.displayName = SliderPrimitive.Root.displayName;
export { Slider };